<template>

    <div class="analytics-card border rounded shadow" :style="{ backgroundColor: styles.chartBackground }">
        <div class="settings-button" @click.stop="openModal">
            <font-awesome-icon icon="cog" style="width: 16px; height: 16px; color: white" />
        </div>
        <div class="card-close-button" @click.stop="removeCard">
            <font-awesome-icon icon="times" style="width: 20px; height: 20px; color: white" />
        </div>
        <div style="height: 100%">
            <component :height="height" :options="chartOptions" :chartdata="chartData" :key="chartKey"
                :is="selectedChartType" :styles="{ width: '100%', height: '100%', position: 'relative' }"
                class="mt-1 p-3" />
        </div>

        <!-- Modal -->
        <div v-if="isModalOpen" class="modal-bg" @click.self="closeModal">
            <div class="modal">
                <div class="modal-header">
                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                        <font-awesome-icon icon="cog" style="width: 22px; height: 22px; color: white" />
                        <h2>Custom Chart Settings</h2>
                    </div>
                </div>
                <div class="modal-body">
                    <b-row>
                        <b-col cols="6">
                            <div>
                                <label style="display: block; min-height: 0; margin-bottom: 5px;" for="chart-type">Chart
                                    Type</label>
                                <b-form-select v-model="selectedChartType" :options="[{
                                    text: 'Bar Chart',
                                    value: 'bar-chart'
                                }, {
                                    text: 'Line Chart',
                                    value: 'line-chart'
                                }, {
                                    text: 'Horizontal Bar',
                                    value: 'horizontal-bar'
                                }]"></b-form-select>
                            </div>
                        </b-col>
                        <b-col cols="6">
                            <div class="radio-group">
                                <!-- <p style="min-height: 0; margin-bottom: 5px;">Show Second Y-Axis:</p> -->
                                <!-- <label>
                                    <input type="radio" value="yes" v-model="showSecondYAxis" /> Yes
                                </label>
                                <label>
                                    <input type="radio" value="no" v-model="showSecondYAxis" /> No
                                </label> -->

                                <b-form-group label="Show secondary Y-axis">
                                    <div style="display: flex; gap: 1rem;">
                                        <b-form-radio v-model="showSecondYAxis" :value="true">Yes</b-form-radio>
                                        <b-form-radio v-model="showSecondYAxis" :value="false">No</b-form-radio>
                                    </div>
                                </b-form-group>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row>                        
                        <b-col cols="6">
                            <div>
                                <label style="display: block; min-height: 0; margin-bottom: 5px;"
                                for="y-axis-select">Y-Axis Variable</label>
                                <b-form-select v-model="selectedYAxis" :options="variables"></b-form-select>
                            </div>
                        </b-col>
                        <b-col cols="6">
                            <div>
                                <label style="display: block; min-height: 0; margin-bottom: 5px;"
                                    for="x-axis-select">X-Axis Variable</label>
                                <b-form-select v-model="selectedXAxis" :options="variables"></b-form-select>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row v-if="showSecondYAxis">
                        <b-col cols="6">
                            <div>
                                <label style="display: block; min-height: 0; margin-bottom: 5px;"
                                    for="x-axis-select">Secondary Y-Axis Variable</label>
                                <b-form-select v-model="selectedSecondaryYAxis" :options="variables"></b-form-select>
                            </div>
                        </b-col>
                    </b-row>
                </div>
                <div class="modal-footer">
                    <b-button variant="secondary" @click="closeModal">
                        Cancel
                    </b-button>
                    <b-button variant="primary" @click="applySettings">
                        Apply
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { HTTP } from '@/axios_api/http-common';
import Bar from './charts/Bar.vue';
import HorizontalBar from './charts/HorizontalBar.vue';
import Line from './charts/Line.vue';
import { defaultChartData, defaultChartOptions } from './charts/chartUtils';

export default {
    props: {
        styles: {
            type: Object,
            default: () => ({})
        },
        selectedProjectId: {
            type: String,
            default: ''
        }
    },
    components: {
        'bar-chart': Bar,
        'line-chart': Line,
        'horizontal-bar': HorizontalBar,
    },
    data() {
        return {
            chartKey: 1,
            isModalOpen: false,
            selectedChartType: 'line-chart',
            selectedXAxis: "",
            selectedYAxis: "",
            selectedSecondaryYAxis: "",
            showSecondYAxis: false,
            height: 300,
            width: 400,
            colorArray: ['#063567', '#fd7e14', '#CDFF4B'],
            chartData: JSON.parse(JSON.stringify(defaultChartData)),
            chartOptions: JSON.parse(JSON.stringify(defaultChartOptions)),
            customChartRawData: []
        }
    },
    methods: {
        openModal() {
            this.isModalOpen = true;
        },
        closeModal() {
            this.isModalOpen = false;
        },
        applySettings() {
            // Logic to apply selected settings
            this.chartOptions.scales.yAxes[1].display = this.showSecondYAxis;
            this.chartKey = this.chartKey + 1;
            this.isModalOpen = false;
        },
        removeCard() {
            this.$emit('on-remove-custom-chart-card')
        },
        getCustomChartData() {
            if (!this.selectedProjectId) return;
            HTTP.get("analytics/query/dashboard" + "/?projectId=" + this.selectedProjectId)
                .then((res) => {
                    this.customChartRawData = res.data.data;
                    if (this.customChartRawData.length) {
                        this.selectedXAxis = this.customChartRawData[0].title;
                        this.selectedYAxis = this.customChartRawData[1].title;
                        this.selectedSecondaryYAxis = this.customChartRawData[2].title;
                    }
                })
                .catch((err) => {
                    window.console.log(err);
                })
        },
        regenerateChart() {
            if (!this.customChartRawData.length || !this.selectedXAxis || !this.selectedYAxis) return;

            const xData = this.customChartRawData.find(c => c.title === this.selectedXAxis)
            const yData = this.customChartRawData.find(c => c.title === this.selectedYAxis)
            const ySecondaryData = this.customChartRawData.find(c => c.title === this.selectedSecondaryYAxis)
            this.chartOptions.title.text = `${yData.title} vs ${xData.title}`
            this.chartOptions.scales.xAxes[0].scaleLabel.labelString = xData.type;
            this.chartOptions.scales.yAxes[0].scaleLabel.labelString = yData.type;
            if (this.showSecondYAxis) {
                this.chartOptions.scales.yAxes[1].scaleLabel.labelString = ySecondaryData.type;
            }

            const result = {
                labels: xData.data,
                datasets: [
                    {
                        label: yData.title,
                        backgroundColor: this.selectedChartType === 'line-chart' ? 'transparent' : this.colorArray[0],
                        borderColor: this.colorArray[0],
                        borderWidth: 2,
                        lineTension: 0,
                        data: yData.data,
                    },
                ],
            }

            if (this.showSecondYAxis && ySecondaryData) {
                result.datasets.push({
                        label: ySecondaryData.title,
                        backgroundColor: this.selectedChartType === 'line-chart' ? 'transparent' : this.colorArray[2],
                        borderColor: this.colorArray[2],
                        borderWidth: 2,
                        lineTension: 0,
                        data: ySecondaryData.data,
                        yAxisID: 'y-axis-2'
                    })
                this.chartOptions.scales.yAxes[1].display = this.showSecondYAxis;
            }
            else {
                this.chartOptions.scales.yAxes[1].display = this.showSecondYAxis;
            }

            this.chartKey = this.chartKey + 1;
            this.chartData = JSON.parse(JSON.stringify(result));
        }
    },
    computed: {
        variables() {
            if (!this.customChartRawData.length) return []
            return this.customChartRawData.map(r => r.title)
        },
    },
    mounted() {
    },
    watch: {
        selectedProjectId: {
            immediate: true,
            handler() {
                this.getCustomChartData();
            }
        },
        selectedXAxis: {
            handler() {
                this.regenerateChart()
            }
        },
        selectedChartType: {
            handler() {
                this.regenerateChart()
            }
        },
        selectedYAxis: {
            handler() {
                this.regenerateChart()
            }
        },
        selectedSecondaryYAxis: {
            handler() {
                this.regenerateChart()
            }
        },
        showSecondYAxis: {
            handler() {
                this.regenerateChart()
            }
        },
        customChartRawData: {
            immediate: true,
            handler() {
                this.regenerateChart()
            }
        }
    }
}
</script>

<style scoped>
.analytics-card {
    padding: 1rem;
    height: 450px;
    width: 100%;
    position: relative;
}

.analytics-card:active {
    cursor: move;
}

.on-top {
    z-index: 1000;
}

.chart-container {
    position: relative;
    margin: auto;
}

.settings-button {
    position: absolute;
    top: 5px;
    right: 32px;
    background: rgb(83, 83, 190);
    width: 22px;
    height: 22px;
    border-radius: 100%;
    z-index: 2000;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-close-button {
    position: absolute;
    top: 5px;
    right: 5px;
    background: red;
    width: 22px;
    height: 22px;
    border-radius: 100%;
    z-index: 2000;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}


/* Modal Styles */
.modal-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
}

.modal {
    background: white;
    border-radius: 8px;
    width: 550px;
    height: fit-content;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
}

.modal-header {
    margin-bottom: 16px;
    padding-inline: 0;
    background-color: #19212b;
    color: white;
    padding-inline: 20px;
}

.modal-header h2 {
    font-size: 1.075rem;
    text-align: center;
    margin-bottom: 0;
}

.modal-body {
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.radio-group label {
    margin-right: 10px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.modal select {
    width: 100%;
    height: 40px;
    padding-inline: 0.5rem;
    background-color: #eee;
    border-color: #ddd;
    border-radius: 6px;
}

button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

button:hover {
    opacity: 0.9;
}

.settings-button,
.card-close-button {
    cursor: pointer;
}
</style>